import { ofType } from '@ngrx/effects';
import { ActionWithPayload } from '@util/actions';
import { getCorrelationId, hasCorrelationId } from '@util/correlation-id-helper';
import { hasValue } from '@util/type';
import * as R from 'ramda';
import { combineLatest, Observable, ObservableInput, OperatorFunction, pipe } from 'rxjs';
import { filter, groupBy, map, mergeMap, scan, take } from 'rxjs/operators';

const typeInPayload = (payloadType: string) => R.allPass([hasValue, R.propEq('type', payloadType)]);
const actionOfType = actionType => R.propEq('type', actionType);
const getType = R.prop('type');
const containsAll = (currentActionTypes: any[], neededActionTypes: any[]) => R.all(action => R.includes(action, currentActionTypes) , neededActionTypes);

/**
 * Filter actions by type of the action and defined type in the payload.
 * @param actionType type of the action
 * @param payloadType defined type of the payload
 * @returns {OperatorFunction<any, any>}
 */
export const pcsOfType = <T extends ActionWithPayload<any>>(actionType, payloadType) => filter((action: T) =>
  actionOfType(actionType)(action) && typeInPayload(payloadType)(action.payload)
);

/**
 * Map an action to its payload.
 *
 * @return {OperatorFunction<ActionWithPayload<T>, T>} operator function that maps the given action to its payload
 */
export const mapPayload = <T>() => map<ActionWithPayload<T>, T>(R.prop('payload'));

/**
 * @return {MonoTypeOperatorFunction<T>} operator function that returns an Observable that emits only the first
 * value emitted by the source Observable.
 */
export const takeOne = <T>() => take<T>(1);

/**
 * Operator similar to withLatestFrom. The difference is that unlike withLatestFrom, it lazily evaluates the input
 * Observables.
 */
export const withLatestFromStore = <SOURCE, MERGE extends ObservableInput<any>[]>(
  ...toBeMerged$: [...MERGE]
): OperatorFunction<SOURCE, [SOURCE, ...{ [P in keyof MERGE]: MERGE[P] extends ObservableInput<infer U> ? U : never }]> =>
  mergeMap((source) =>
    combineLatest(toBeMerged$).pipe(
      takeOne(),
      map((source2): any => [source, ...source2]),
    )
  );

/**
 * Operator that groups actions of the given types by correlation id.
 */
export const groupByCorrelationId = (actionTypes: string[]) => pipe(
  ofType(...actionTypes),
  filter(action => hasCorrelationId(action)),
  groupBy(action => getCorrelationId(action)),
  mergeMap((group$: Observable<any>) => group$.pipe(
    scan((allActionsInGroup, action) => [...allActionsInGroup, action], []),
    filter(actions => {
      const currentActionTypes = R.map(getType, actions);
      return containsAll(currentActionTypes, actionTypes);
    })
  )));
